import { createStore, useSelector } from '@xstate/store-react';
import {
  ApplianceFormData,
  AppStatus,
  ConfirmationPurchaseData,
  initialContext,
  NextEventData,
  QuoteStatus,
  StoreContext,
  StoreEvent,
} from './store.types';
import { saveSession, loadSession, clearSession } from './session';
import { CatalogueCategory } from '../models/services/api/getCatalogue';
import { getSessionId } from '../services/api/getSessionId';
import { getCatalogue } from '../services/api/getCatalogue';
import { FormKey } from '../enums/formKey';
import { StepKey } from '../enums/stepKey';
import { queryModelNumber } from '../services/api/queryModelNumber';
import { ApplianceModel } from '../models/services/api/queryModelNumber';
import { formStepRoutes, getNextStepPath, getPreviousStepPath } from '../routes/routes';
import { getSystemCode } from '../utils/getSystemCode';
import { formatQuoteRequest } from '../utils/quote/formatQuoteRequest';
import { postQuote } from '../services/api/postQuote';
import { QuoteResponse, RegOnlyResponse } from '../models/services/api/postQuote';
import { postRegistria } from '../services/api/postRegistria';
import { RegistriaConciergeResponse } from '../models/services/api/postRegistria';
import { CpgData } from '../models/services/api/getCpgFinder';
import { getCpgData } from '../services/api/getCpgFinder';
import { getAuthToken } from '../services/api/getAuthToken';
import { InitOptions } from '@domgen/checkout-sdk-types';
import { CheckoutSuccess } from '../pages/CheckoutPage/CheckoutResponses';
import { AppRoute } from '../enums/appRoutes';
import { log } from '@domgen/shared/utils/log';
import posthog from 'posthog-js';
import { adobeTrackPurchaseCompletion, adobeTrackRegistration } from '../services/adobeTags/track';
import { capturePostHogEvent } from '../services/posthog/capture';

// https://domgen.atlassian.net/browse/UEO-1047
const SESSION_EXPIRY_MINUTES = 55;

const createAppStore = () =>
  createStore({
    context: initialContext,
    emits: {
      navigate: ({ path, replace = false }: { path: string; replace?: boolean }) => ({
        type: 'navigate',
        data: { path, replace },
      }),
    },
    on: {
      // =======================================================================
      // APP INITIALISATION
      // =======================================================================

      [StoreEvent.INITIALISE]: (context, _, enqueue) => {
        log.debug({ message: StoreEvent.INITIALISE });
        const params = new URLSearchParams(window.location.search);
        const mn = params.get('mn') ?? undefined;
        const sn = params.get('sn') ?? undefined;
        const rs = params.get('rs') ?? undefined;

        const urlParams = mn || sn || rs ? { mn, sn, rs } : undefined;
        const hasUrlParams = !!(mn || sn);

        enqueue.effect(async () => {
          // If entering with URL params (mn + sn), always start fresh
          if (hasUrlParams) {
            clearSession();
            store.send({ type: StoreEvent.CREATE_SESSION });
            return;
          }

          const persisted = loadSession();

          if (persisted?.context.app.session?.dateCreated) {
            const sessionCreated = new Date(persisted.context.app.session.dateCreated).getTime();
            const ageMinutes = (Date.now() - sessionCreated) / 60000;

            if (ageMinutes < SESSION_EXPIRY_MINUTES) {
              const remainingMs =
                SESSION_EXPIRY_MINUTES * 60 * 1000 - (Date.now() - sessionCreated);
              store.send({
                type: StoreEvent.RESTORE_SESSION,
                data: {
                  ...persisted.context,
                  app: { ...persisted.context.app, status: AppStatus.READY },
                },
              });
              store.send({
                type: StoreEvent.START_SESSION_TIMER,
                data: { duration: remainingMs },
              });
              return;
            }
          }

          clearSession();
          store.send({ type: StoreEvent.CREATE_SESSION });
        });

        return {
          ...context,
          urlParams,
          app: { ...context.app, status: AppStatus.LOADING },
        };
      },

      [StoreEvent.CREATE_SESSION]: (context, _, enqueue) => {
        log.debug({ message: StoreEvent.CREATE_SESSION });
        enqueue.effect(async () => {
          try {
            const id = await getSessionId();
            store.send({ type: StoreEvent.SET_SESSION, data: { id } });
          } catch {
            store.send({ type: StoreEvent.SESSION_CREATION_FAILED });
          }
        });

        return {
          ...context,
          app: { ...context.app, status: AppStatus.LOADING, session: null },
        };
      },

      [StoreEvent.SET_SESSION]: (context, event: { data: { id: string } }, enqueue) => {
        log.debug({ message: StoreEvent.SET_SESSION, data: event.data });
        const session = {
          id: event.data.id,
          dateCreated: new Date().toISOString(),
        };

        enqueue.effect(async () => {
          if (!context.app.catalogue) {
            store.send({ type: StoreEvent.GET_CATALOGUE });
          }
          store.send({ type: StoreEvent.START_SESSION_TIMER });
        });

        const updatedContext = {
          ...context,
          app: {
            ...context.app,
            session,
            status: context.app.catalogue ? AppStatus.READY : AppStatus.LOADING,
          },
        };

        if (updatedContext.app.catalogue) {
          saveSession(updatedContext);
        }

        posthog.register({ appSessionId: session.id });
        return updatedContext;
      },

      [StoreEvent.SESSION_CREATION_FAILED]: (context, __, enqueue) => {
        log.debug({ message: StoreEvent.SESSION_CREATION_FAILED });
        enqueue.emit.navigate({ path: AppRoute.ERROR, replace: true });
        return {
          ...context,
          app: {
            ...context.app,
            status: AppStatus.ERROR,
          },
        };
      },

      [StoreEvent.GET_CATALOGUE]: (context, _, enqueue) => {
        log.debug({ message: StoreEvent.GET_CATALOGUE });
        enqueue.effect(async () => {
          try {
            const brandCatalogue = await getCatalogue();
            store.send({ type: StoreEvent.SET_CATALOGUE, data: brandCatalogue });
          } catch (error) {
            console.warn('Failed to fetch catalogue', error);
            store.send({ type: StoreEvent.GET_CATALOGUE_FAILED });
          }
        });

        return context;
      },

      [StoreEvent.SET_CATALOGUE]: (context, event: { data: CatalogueCategory[] }, enqueue) => {
        log.debug({ message: StoreEvent.SET_CATALOGUE, data: event.data });
        const hasUrlParams = !!(
          context.urlParams?.mn ||
          context.urlParams?.sn ||
          context.urlParams?.rs
        );

        if (hasUrlParams) {
          enqueue.effect(async () => {
            store.send({ type: StoreEvent.PROCESS_URL_PARAMS });
          });
        }

        const updatedContext = {
          ...context,
          app: {
            ...context.app,
            catalogue: event.data,
            status: hasUrlParams ? AppStatus.LOADING : AppStatus.READY,
          },
        };

        saveSession(updatedContext);
        return updatedContext;
      },

      [StoreEvent.GET_CATALOGUE_FAILED]: (context, __, enqueue) => {
        log.debug({ message: StoreEvent.GET_CATALOGUE_FAILED });
        enqueue.emit.navigate({ path: AppRoute.ERROR, replace: true });
        return {
          ...context,
          app: {
            ...context.app,
            status: AppStatus.ERROR,
          },
        };
      },

      // =======================================================================
      // URL PARAMS
      // =======================================================================

      [StoreEvent.PROCESS_URL_PARAMS]: (context, _, enqueue) => {
        log.debug({ message: StoreEvent.PROCESS_URL_PARAMS });

        const mn = context.urlParams?.mn;
        const sn = context.urlParams?.sn;

        if (!mn) {
          const form = { ...context.form };

          if (sn) {
            form[StepKey.MODEL_SERIAL] = {
              ...form[StepKey.MODEL_SERIAL],
              [FormKey.SERIAL_NUMBER]: sn,
            };
          }

          const updatedContext = {
            ...context,
            form,
            app: { ...context.app, status: AppStatus.READY },
          };

          saveSession(updatedContext);
          return updatedContext;
        }

        enqueue.effect(async () => {
          const response = await queryModelNumber(mn);
          const matchedAppliance =
            response.appliances.find((a) => mn === a.modelNumber) ??
            response.appliances.find((a) => mn.includes(a.modelNumber));

          store.send({
            type: StoreEvent.SET_URL_PARAM_RESULTS,
            data: {
              appliances: response.appliances,
              matchedAppliance,
            },
          });
        });

        return context;
      },

      [StoreEvent.SET_URL_PARAM_RESULTS]: (
        context,
        event: {
          data: {
            appliances: ApplianceModel[];
            matchedAppliance?: ApplianceModel;
          };
        },
      ) => {
        log.debug({ message: StoreEvent.SET_URL_PARAM_RESULTS, data: event.data });
        const { appliances, matchedAppliance } = event.data;
        const mn = context.urlParams?.mn;
        const sn = context.urlParams?.sn;

        const form = { ...context.form };

        form[StepKey.MODEL_SERIAL] = {
          [FormKey.MODEL_NUMBER]: mn,
          [FormKey.SERIAL_NUMBER]: sn,
        };

        if (matchedAppliance && context.app.catalogue) {
          const category = getCategoryFromApplianceCodeInCatalogue(
            matchedAppliance.itemTypeCode,
            context.app.catalogue,
          );

          form[StepKey.APPLIANCE_DETAILS] = {
            [FormKey.ITEM_CATEGORY]: matchedAppliance.itemTypeCode,
            [FormKey.SUPER_CATEGORY]: category?.categoryCode,
          };
        }

        const updatedContext = {
          ...context,
          form,
          urlParams: {
            ...context.urlParams,
            matchedAppliance,
          },
          applianceLookupResults: appliances,
          app: { ...context.app, status: AppStatus.READY },
        };

        saveSession(updatedContext);
        return updatedContext;
      },

      [StoreEvent.SAVE_APPLIANCE_MODEL_RESULTS]: (context, event: { data: ApplianceModel[] }) => {
        log.debug({ message: StoreEvent.SAVE_APPLIANCE_MODEL_RESULTS, data: event.data });
        return {
          ...context,
          applianceLookupResults: [...event.data],
        };
      },

      // =======================================================================
      // FORM NAVIGATION
      // =======================================================================

      [StoreEvent.NEXT]: (context, event: { data: NextEventData }, enqueue) => {
        log.debug({ message: StoreEvent.NEXT, data: event.data });
        const stepKey = Object.keys(event.data)[0] as StepKey;
        const form = {
          ...context.form,
          ...event.data,
        };

        if (stepKey === StepKey.MODEL_SERIAL) {
          const modelSerial = form[StepKey.MODEL_SERIAL];
          const modelNumber = modelSerial?.[FormKey.MODEL_NUMBER];

          if (modelNumber && context.applianceLookupResults) {
            const matchedAppliance = context.applianceLookupResults.find(
              (a) => a.modelNumber === modelNumber,
            );

            if (matchedAppliance && context.app.catalogue) {
              const category = getCategoryFromApplianceCodeInCatalogue(
                matchedAppliance.itemTypeCode,
                context.app.catalogue,
              );

              if (category) {
                form[StepKey.APPLIANCE_DETAILS] = {
                  [FormKey.SUPER_CATEGORY]: category.categoryCode,
                  [FormKey.ITEM_CATEGORY]: matchedAppliance.itemTypeCode,
                };
              }
            }
          }
        }

        const updatedContext = {
          ...context,
          form,
          quote: undefined,
        };

        saveSession(updatedContext);
        const nextPath = getNextStepPath(stepKey);
        if (nextPath) {
          enqueue.emit.navigate({ path: nextPath });
        }
        return updatedContext;
      },

      [StoreEvent.BACK]: (context, event: { data: { stepKey: StepKey } }, enqueue) => {
        log.debug({ message: StoreEvent.BACK, data: event.data });
        const previousStep = getPreviousStepPath(event.data.stepKey);
        if (previousStep) {
          enqueue.emit.navigate({ path: previousStep });
        }

        return context;
      },

      // =======================================================================
      // QUOTE SUBMISSION
      // =======================================================================

      [StoreEvent.SUBMIT]: (
        context,
        event: {
          data: {
            [StepKey.PERSONAL_DETAILS]: ApplianceFormData[StepKey.PERSONAL_DETAILS];
          };
        },
        enqueue,
      ) => {
        log.debug({ message: StoreEvent.SUBMIT, data: event.data });
        const form = {
          ...context.form,
          ...event.data,
        };

        enqueue.effect(async () => {
          try {
            const systemCode = getSystemCode(context.urlParams?.rs);
            const quoteRequest = formatQuoteRequest({ ...context.form, ...event.data }, systemCode);

            if (!quoteRequest) {
              store.send({ type: StoreEvent.QUOTE_FAILED });
              return;
            }

            const response = await postQuote(quoteRequest);
            const brandCode = import.meta.env.VITE_BRAND_CODE as
              | 'WHIRL'
              | 'KHAID'
              | 'MYTAG'
              | 'AMANA';

            if ('statusCode' in response && response.statusCode === 201) {
              const regOnly = response as RegOnlyResponse;
              store.send({ type: StoreEvent.NO_QUOTE_REG_ONLY, data: regOnly });
              capturePostHogEvent(posthog, 'registration-without-quote', store.get()); // no quote infers a successful registration
              adobeTrackRegistration({
                modelNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] ?? '',
                serialNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] ?? '',
                purchasePrice: form[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE] ?? '',
                dateDelivered: form[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] ?? '',
                city: form[StepKey.PERSONAL_DETAILS]?.[FormKey.CITY] ?? '',
                state: form[StepKey.PERSONAL_DETAILS]?.[FormKey.STATE_CODE] ?? '',
                postalCode: form[StepKey.PERSONAL_DETAILS]?.[FormKey.POSTAL_CODE] ?? '',
              });
              const postRegRequest = {
                personId: regOnly.personId,
                brand: brandCode,
                locale: navigator.language.toLowerCase(),
                itemId: regOnly.itemId,
              };

              try {
                const concierge = await postRegistria(postRegRequest);
                store.send({ type: StoreEvent.SET_CONCIERGE_DATA, data: concierge });
              } catch (error) {
                log.error({ message: 'Failed to fetch registria data', data: error });
              }
            } else if ('statusCode' in response && response.statusCode !== 200) {
              console.error('Quote request failed with status code', response.statusCode);
              store.send({ type: StoreEvent.QUOTE_FAILED });
              capturePostHogEvent(posthog, 'registration-failure', store.get());

              return;
            } else {
              const quoteData = response as QuoteResponse;
              store.send({ type: StoreEvent.SET_QUOTE, data: quoteData });
              capturePostHogEvent(posthog, 'registration-with-quote', store.get());
              adobeTrackRegistration({
                modelNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] ?? '',
                serialNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] ?? '',
                purchasePrice: form[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE] ?? '',
                dateDelivered: form[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE] ?? '',
                city: form[StepKey.PERSONAL_DETAILS]?.[FormKey.CITY] ?? '',
                state: form[StepKey.PERSONAL_DETAILS]?.[FormKey.STATE_CODE] ?? '',
                postalCode: form[StepKey.PERSONAL_DETAILS]?.[FormKey.POSTAL_CODE] ?? '',
              });
              const itemId = quoteData.items?.[0]?.id;
              const postRegRequest = {
                personId: quoteData.personId,
                brand: brandCode,
                locale: navigator.language.toLowerCase(),
                ...(itemId && { itemId }),
              };
              try {
                const concierge = await postRegistria(postRegRequest);
                store.send({ type: StoreEvent.SET_CONCIERGE_DATA, data: concierge });
              } catch (error) {
                log.error({ message: 'Failed to fetch registria data', data: error });
              }
            }

            const modelNumber = form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER];
            if (modelNumber) {
              try {
                const cpgResponse = await getCpgData(modelNumber);
                if (typeof cpgResponse !== 'string') {
                  store.send({ type: StoreEvent.SET_CPG_DATA, data: cpgResponse });
                }
              } catch (error) {
                console.error('Failed to fetch CPG data', error);
              }
            }
          } catch (error) {
            console.error('Failed to submit quote', error);
            store.send({ type: StoreEvent.QUOTE_FAILED });
          }
        });

        const updatedContext = {
          ...context,
          form,
          quote: { status: QuoteStatus.LOADING },
          confirmation: {
            applianceDetails: {
              applianceName: getFormApplianceName(context),
              categoryName: getFormCategoryName(context),
              serialNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER],
              modelNumber: form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER],
              arrivalDate: form[StepKey.ARRIVAL_DATE]?.[FormKey.ARRIVAL_DATE],
              purchasePrice: form[StepKey.PURCHASE_PRICE]?.[FormKey.PURCHASE_PRICE],
            },
          },
        };

        return updatedContext;
      },

      [StoreEvent.QUOTE_FAILED]: (context, _, enqueue) => {
        log.debug({ message: StoreEvent.QUOTE_FAILED });
        enqueue.emit.navigate({ path: AppRoute.ERROR });
        return { ...context, quote: { status: QuoteStatus.ERROR } };
      },

      [StoreEvent.SET_QUOTE]: (context, event: { data: QuoteResponse }, enqueue) => {
        const quote = event.data;
        const subscription = quote?.offer?.contracts[0]?.contractAttributes.subscription;

        const paymentMethod = subscription?.cc?.discountProfileAttributes
          ? 'cc'
          : subscription?.ach?.discountProfileAttributes
            ? 'ach'
            : 'cc';

        /*
         * This is potentially incorrect as we are falling back to showing an ACH discount on the Quote page if the CC
         * discount isn't present. However here there are some attributes that will always be present like
         * {subscription?.cc?.netFirstPaymentAmount} so this will always be set as the netFirstPaymentAmount for the
         * confirmation page.
         */
        const offerData = {
          subscription,
          startDate: quote?.offer.contracts[0]?.contractAttributes.effectiveDate?.split('T')[0],
          netFirstPaymentAmount: subscription?.[paymentMethod]?.netFirstPaymentAmount,
          netFirstPaymentAmountAfterPromotionalPeriod:
            subscription?.[paymentMethod]?.discountProfileAttributes
              ?.netFirstPaymentAmountAfterPromotionalPeriod,
          discountCode: subscription
            ? [
                subscription.cc?.discountProfileAttributes?.ccDiscountCodeApplied,
                subscription.ach?.discountProfileAttributes?.achDiscountCodeApplied,
              ]
                .filter((code): code is string => !!code && code.toString().trim().length > 0)
                .join(' | ')
            : '',
        };

        enqueue.emit.navigate({ path: AppRoute.QUOTE });

        const updatedContext = {
          ...context,
          quote: {
            status: QuoteStatus.READY,
            data: quote,
          },
          confirmation: {
            ...context.confirmation,
            offerData,
          },
          gaEventData: {
            ...context.gaEventData,
            netFirstPaymentAmount: subscription?.[paymentMethod]?.netFirstPaymentAmount,
          },
        };

        saveSession(updatedContext);

        return updatedContext;
      },

      [StoreEvent.NO_QUOTE_REG_ONLY]: (context, event: { data: RegOnlyResponse }, enqueue) => {
        log.debug({ message: StoreEvent.NO_QUOTE_REG_ONLY, data: event.data });
        enqueue.emit.navigate({ path: AppRoute.CONFIRMATION });
        const updatedContext = {
          ...context,
          confirmation: {
            ...context.confirmation,
            regOnlyData: event.data,
          },
        };

        saveSession(updatedContext);

        return updatedContext;
      },

      [StoreEvent.SET_CONCIERGE_DATA]: (context, event: { data: RegistriaConciergeResponse }) => {
        log.debug({ message: StoreEvent.SET_CONCIERGE_DATA, data: event.data });
        return {
          ...context,
          confirmation: {
            ...context.confirmation,
            conciergeData: event.data,
          },
        };
      },

      [StoreEvent.SET_CPG_DATA]: (context, event: { data: CpgData }) => {
        log.debug({ message: StoreEvent.SET_CPG_DATA, data: event.data });
        return {
          ...context,
          confirmation: {
            ...context.confirmation,
            cpgData: event.data,
          },
        };
      },

      [StoreEvent.REG_ONLY]: (context, _, enqueue) => {
        log.debug({ message: StoreEvent.REG_ONLY });
        const quote = context.quote?.data;

        enqueue.emit.navigate({ path: AppRoute.CONFIRMATION });

        const updatedContext = {
          ...context,
          confirmation: {
            ...context.confirmation,
            regOnlyData: {
              personId: quote?.personId ?? '',
              itemId: quote?.items?.[0]?.id ?? '',
            },
          },
        };

        saveSession(updatedContext);
        return updatedContext;
      },

      // =======================================================================
      // CHECKOUT
      // =======================================================================
      [StoreEvent.CONTINUE_TO_CHECKOUT]: (
        context,
        event: { data: { continueToCheckoutFromModal: boolean } },
        enqueue,
      ) => {
        log.debug({ message: StoreEvent.CONTINUE_TO_CHECKOUT });
        enqueue.effect(async () => {
          try {
            const quote = context.quote?.data;
            if (!quote) {
              store.send({ type: StoreEvent.CHECKOUT_FAILED });
              return;
            }

            const personId = quote.personId;
            const offerReference = quote.offer.offerReference;
            const offer = quote.offer;
            const customer = context.form[StepKey.PERSONAL_DETAILS];
            const brandCode = import.meta.env.VITE_BRAND_CODE as
              | 'WHIRL'
              | 'KHAID'
              | 'MYTAG'
              | 'AMANA';

            if (!personId || !offerReference || !offer || !customer) {
              store.send({ type: StoreEvent.CHECKOUT_FAILED });
              return;
            }

            const { token } = await getAuthToken({
              personId,
              offerReferences: [offerReference],
              brand: brandCode,
            });

            const subs = { ...offer.contracts[0].contractAttributes.subscription };
            const offers = [
              {
                ...offer,
                contracts: [
                  {
                    ...offer.contracts[0],
                    contractAttributes: {
                      ...offer.contracts[0].contractAttributes,
                      ...subs,
                    },
                  },
                ],
              },
            ];

            const checkoutData: InitOptions = {
              proposition: { type: 'monthly_subs' },
              authToken: token,
              basket: {
                channelSourceData: {
                  checkoutSourceRef: `wpus-usa-${import.meta.env.VITE_BRAND_CODE.trim().toLowerCase()}-olr`,
                  interactionId: context.app.session?.id ?? quote.interactionId,
                },
                offers,
              },
              customer: {
                title: '',
                firstName: customer[FormKey.FIRST_NAME],
                lastName: customer[FormKey.LAST_NAME],
                emailAddress: customer[FormKey.EMAIL],
                personId,
                address: {
                  countryCode: customer[FormKey.COUNTRY_CODE] ?? 'USA',
                  number: customer[FormKey.ADDRESS_LINE_1],
                  street: customer[FormKey.ADDRESS_LINE_2],
                  postalCode: customer[FormKey.POSTAL_CODE],
                  stateCode: customer[FormKey.STATE_CODE],
                  city: customer[FormKey.CITY],
                },
              },
            };

            store.send({ type: StoreEvent.SET_CHECKOUT, data: checkoutData });
          } catch (error) {
            console.error('Error during checkout process', error);
            store.send({ type: StoreEvent.CHECKOUT_FAILED });
          }
        });

        enqueue.emit.navigate({ path: AppRoute.CHECKOUT });

        return {
          ...context,
          checkout: { loading: true },
          confirmation: {
            ...context.confirmation,
            offerData: {
              ...context.confirmation?.offerData,
            },
          },
          gaEventData: {
            ...context?.gaEventData,
            salesWidgetClicked: event.data.continueToCheckoutFromModal,
          },
        };
      },

      [StoreEvent.SET_CHECKOUT]: (context, event: { data: InitOptions }) => {
        log.debug({ message: StoreEvent.SET_CHECKOUT, data: event.data });
        return {
          ...context,
          checkout: {
            loading: false,
            data: event.data,
          },
        };
      },

      [StoreEvent.CHECKOUT_SUCCESS]: (context, event: { data: CheckoutSuccess }, enqueue) => {
        log.debug({ message: StoreEvent.CHECKOUT_SUCCESS, data: event.data });
        const quote = context.quote?.data;
        const checkoutSuccess = event.data;
        const paymentType = checkoutSuccess.billing.paymentMethodUsed;
        const mappedPaymentType = paymentType === 'card' ? 'cc' : 'ach';
        const planNumber = checkoutSuccess.contracts[0].contractId;
        const transactionId = checkoutSuccess.transactionId;

        const chosenSubscription =
          quote?.offer?.contracts[0]?.contractAttributes.subscription?.[mappedPaymentType];

        const purchaseData: ConfirmationPurchaseData = {
          planNumber,
          transactionId,
          paymentType,
          mappedPaymentType,
          netFirstPaymentAmountFromPurchase: chosenSubscription?.netFirstPaymentAmount,
          netFirstPaymentAmountAfterPromotionalPeriodFromPurchase:
            chosenSubscription?.discountProfileAttributes
              ?.netFirstPaymentAmountAfterPromotionalPeriod,
          discountCodeFromPurchase:
            chosenSubscription?.discountProfileAttributes?.[
              mappedPaymentType === 'cc' ? 'ccDiscountCodeApplied' : 'achDiscountCodeApplied'
            ],
          discountTermFromPurchase: chosenSubscription?.discountProfileAttributes?.discountTerm,
        };

        if (planNumber) {
          enqueue.effect(async () => {
            try {
              await postRegistria({
                locale: navigator.language.toLowerCase(),
                contractId: planNumber,
              });
            } catch (error) {
              log.error({ message: 'Failed to post Registria contract registration', data: error });
            }
          });
        }

        const appliance = context.applianceLookupResults?.find(
          (model) =>
            model.modelNumber === context.form?.[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER],
        );

        const guarantee =
          'items' in event.data ? context.quote?.data?.items?.[0].guarantee : appliance?.guarantee;

        const { regOnlyData, ...confirmation } = context.confirmation || {};

        const updatedContext = {
          ...context,
          form: {},
          applianceLookupResults: undefined,
          quote: undefined,
          checkout: undefined,
          confirmation: {
            ...confirmation,
            purchaseData,
            applianceDetails: {
              ...confirmation?.applianceDetails,
              guarantee: guarantee ?? 12,
            },
          },
        };

        saveSession(updatedContext);
        adobeTrackPurchaseCompletion({
          modelNumber: context.form[StepKey.MODEL_SERIAL]?.[FormKey.MODEL_NUMBER] ?? '',
          espSku: context.form[StepKey.MODEL_SERIAL]?.[FormKey.SERIAL_NUMBER] ?? '',
          productSku: [],
        });
        enqueue.emit.navigate({ path: AppRoute.CONFIRMATION, replace: true });

        return updatedContext;
      },

      [StoreEvent.CHECKOUT_FAILED]: (context, _, enqueue) => {
        log.debug({ message: StoreEvent.CHECKOUT_FAILED });
        enqueue.emit.navigate({ path: AppRoute.ERROR, replace: true });
        return context;
      },

      // =======================================================================
      // SESSION MANAGEMENT
      // =======================================================================

      [StoreEvent.START_SESSION_TIMER]: (
        context,
        event: { data?: { duration?: number } },
        enqueue,
      ) => {
        log.debug({ message: StoreEvent.START_SESSION_TIMER, data: event.data });
        const duration = event.data?.duration ?? SESSION_EXPIRY_MINUTES * 60 * 1000;

        enqueue.effect(() => {
          const timer = setTimeout(() => {
            store.send({ type: StoreEvent.SESSION_EXPIRED });
          }, duration);

          return () => clearTimeout(timer);
        });

        return context;
      },

      [StoreEvent.SESSION_EXPIRED]: (context, _, enqueue) => {
        log.debug({ message: StoreEvent.SESSION_EXPIRED });
        clearSession();

        enqueue.effect(async () => {
          store.send({ type: StoreEvent.CREATE_SESSION });
        });

        return {
          ...initialContext,
          app: {
            ...initialContext.app,
            catalogue: context.app.catalogue,
            status: AppStatus.LOADING,
          },
        };
      },

      [StoreEvent.RESTORE_SESSION]: (_, event: { data: StoreContext }) => {
        log.debug({ message: StoreEvent.RESTORE_SESSION, data: event.data });
        posthog.register({ appSessionId: event.data.app.session?.id });
        return {
          ...event.data,
          app: {
            ...event.data.app,
            status: AppStatus.READY,
          },
        };
      },
    },
  });

// ---------------------------------------------------------------------------
// Singleton with HMR support
// ---------------------------------------------------------------------------

let storeInstance: ReturnType<typeof createAppStore> | undefined;

if (import.meta.hot?.data?.storeInstance) {
  storeInstance = import.meta.hot.data.storeInstance;
}

if (!storeInstance) {
  storeInstance = createAppStore();
  if (import.meta.hot?.data) {
    import.meta.hot.data.storeInstance = storeInstance;
  }
}

export const store = storeInstance;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const useStoreContext = () => useSelector(store, (state) => state.context);
export const useAppState = () => useSelector(store, (state) => state.context.app);
export const useApplianceFormData = () => useSelector(store, (state) => state.context.form);
export const useApplianceLookupResults = () =>
  useSelector(store, (state) => state.context.applianceLookupResults);
export const useUrlParams = () => useSelector(store, (state) => state.context.urlParams);
export const useQuote = () => useSelector(store, (state) => state.context.quote);
export const useConfirmation = () => useSelector(store, (state) => state.context.confirmation);
export const useCheckout = () => useSelector(store, (state) => state.context.checkout);
export const useGaEventData = () => useSelector(store, (state) => state.context.gaEventData);

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getCategoryFromApplianceCodeInCatalogue(
  applianceCode: string,
  catalogue?: CatalogueCategory[],
): CatalogueCategory | undefined {
  if (catalogue) {
    return catalogue.find((item) =>
      item.appliances?.some((appliance) => appliance.applianceCode === applianceCode),
    );
  } else {
    return undefined;
  }
}

export const formCategoryName = store.select((state) => {
  return getFormCategoryName(state);
});

export const formApplianceName = store.select((state) => {
  return getFormApplianceName(state);
});

const requiredFormFields = {
  [StepKey.APPLIANCE_DETAILS]: [FormKey.SUPER_CATEGORY, FormKey.ITEM_CATEGORY],
  [StepKey.MODEL_SERIAL]: [FormKey.MODEL_NUMBER, FormKey.SERIAL_NUMBER],
  [StepKey.PURCHASE_PRICE]: [FormKey.PURCHASE_PRICE],
  [StepKey.ARRIVAL_DATE]: [FormKey.ARRIVAL_DATE],
  [StepKey.PERSONAL_DETAILS]: [
    FormKey.FIRST_NAME,
    FormKey.LAST_NAME,
    FormKey.EMAIL,
    FormKey.PHONE_DIALLING_CODE,
    FormKey.PHONE_NUMBER,
    FormKey.ADDRESS_LINE_1,
    FormKey.CITY,
    FormKey.STATE_CODE,
    FormKey.POSTAL_CODE,
    FormKey.COUNTRY_CODE,
    FormKey.ADDRESS_IS_FORMATTED,
    FormKey.ADDRESS_ID,
    FormKey.MERCHANDISE_ADDRESS_LINE_1,
    FormKey.MERCHANDISE_CITY,
    FormKey.MERCHANDISE_STATE_CODE,
    FormKey.MERCHANDISE_POSTAL_CODE,
    FormKey.MERCHANDISE_IS_FORMATTED,
    FormKey.MERCHANDISE_ADDRESS_ID,
  ],
};

export const getLatestValidStepIndex = (): number => {
  const formData = store.getSnapshot().context.form;

  for (let i = 0; i < formStepRoutes.length; i++) {
    const stepKey = formStepRoutes[i].stepKey;
    const stepFormData = formData?.[stepKey] as Record<string, unknown> | undefined;
    const requiredFields = requiredFormFields[stepKey];

    if (!stepFormData || !requiredFields.every((field) => stepFormData[field])) {
      return i;
    }
  }

  return formStepRoutes.length - 1;
};

const getFormCategoryName = (state: StoreContext) => {
  const formCategoryCode = state.form?.[StepKey.APPLIANCE_DETAILS]?.[FormKey.SUPER_CATEGORY];
  return state.app.catalogue?.find((cat) => cat.categoryCode === formCategoryCode)?.categoryName;
};

const getFormApplianceName = (state: StoreContext) => {
  const formItemTypeCode = state.form?.[StepKey.APPLIANCE_DETAILS]?.[FormKey.ITEM_CATEGORY];
  const category = state.app.catalogue?.find(
    (cat) => cat.categoryCode === state.form?.[StepKey.APPLIANCE_DETAILS]?.[FormKey.SUPER_CATEGORY],
  );
  return category?.appliances.find((app) => app.applianceCode === formItemTypeCode)?.applianceName;
};
